import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';

interface Props {
  title?: string | null;
  description?: string | null;
  lang?: string;
  image?: string | null;
}

const GetSEOMetadata = graphql`
  query GetSEOMetadata {
    site {
      siteMetadata {
        title
        description
        siteUrl
        keywords
        image
      }
    }
  }
`;

const SEO: React.FC<Props> = ({ description, title, image, lang = 'en' }) => {
  const { site } = useStaticQuery<Queries.GetSEOMetadataQuery>(GetSEOMetadata);
  const metaDescription = description ?? site?.siteMetadata?.description;
  const location = useLocation();

  const imageContent = (image ??
    `${site?.siteMetadata?.siteUrl}${site?.siteMetadata?.image}` ??
    '') as string;

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title ?? site?.siteMetadata?.title ?? ''}
      meta={[
        {
          name: 'description',
          content: metaDescription ?? '',
        },
        {
          name: 'og:description',
          content: metaDescription ?? '',
        },
        {
          name: 'keywords',
          content: site?.siteMetadata?.keywords?.join(','),
        },
        {
          name: 'viewport',
          content: 'user-scalable=no, initial-scale=1, maximum-scale=1, minimum-scale=1',
        },
        {
          name: 'og:url',
          content: `${site?.siteMetadata?.siteUrl}${location.pathname}`,
        },
        {
          name: 'og:type',
          content: 'website',
        },
        {
          name: 'og:title',
          content: title ?? site?.siteMetadata?.title ?? '',
        },
        {
          property: 'og:image',
          content: imageContent,
        },
        {
          property: 'og:image:url',
          content: imageContent,
        },
        {
          property: 'og:image:secure_url',
          content: imageContent,
        },
        {
          property: 'og:image:type',
          content: 'image/jpeg',
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '600',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: imageContent,
        },
      ]}
    >
      <link rel="icon" href="favicon.ico" type="image/x-icon" />
      <script>
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'})
      var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-K4LN64W');`}
      </script>

      <noscript>
        {
          '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-K4LN64W" height="0" width="0" style="display:none;visibility:hidden"></iframe>'
        }
      </noscript>
    </Helmet>
  );
};

export default SEO;
